import styled from 'styled-components';

import { COLORS, DELTA_PILL, SPACE } from '../../../../theme/tokens';
import { Text } from '../../../../components';

/**
 * ONE field grammar for the create form's two rows, defined once so they cannot drift:
 *
 *   Label
 *   [leading slot]  input                                  [trailing slot]
 *
 * The leading slot holds a 16px icon: decorative (`mail`) on the address row, the EYE TOGGLE on the
 * password row (the one interactive leading icon, which earns the position because on a password
 * row the reveal is what the icon would be describing anyway). The trailing slot holds a text
 * affix (`@domain`) or a control (Regenerate).
 *
 * Written here rather than extending the shared `Field`, which has text affixes but no leading
 * icon or leading action slot: this scenario is a deletable folder, and a change to a shared
 * component would outlive the deletion (and is claimed work besides). The row metrics mirror the
 * shared Field's, so a mailbox row beside a shared Field lines up.
 */
export const Root = styled.div<{ $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  ${(p) => (p.$disabled ? 'opacity: 0.55;' : '')}
`;

export const Label = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

export const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  /* Off-scale on purpose: 14px matches the shared Field's vertical inset exactly, so these rows and
     a shared Field would align if they ever share a form. */
  padding: 14px ${SPACE[16]};
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
`;

/** The leading icon box: a fixed 24px square, so a decorative icon and the eye button take the
 *  same room and the input starts at the same x on both rows. */
export const LeadingSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  color: ${COLORS.iconBright};
`;

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${COLORS.text};
  font-size: 16px;
  font-family: inherit;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

/** The trailing slot: an affix or a control; never shrinks, so a long domain truncates the input
 *  rather than the suffix. */
export const TrailingSlot = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  flex: 0 0 auto;
  min-width: 0;
  /* A long domain never takes more than this of the row, so the input keeps a usable width at phone size. */
  max-width: 45%;
`;

/** A text affix in the trailing slot (`@domain`), truncating from the end at phone width. */
export const Affix = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${COLORS.textMuted};
  font-size: 16px;
`;

/** The eye: a real button in the leading slot, named by its aria-label, pressed state exposed, with
 *  a visible focus ring, so an interactive leading icon never reads as decoration to a keyboard. */
export const EyeToggle = styled.button`
  position: relative;
  appearance: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Exactly the leading slot's square, zero padding: the glyph starts at the mail icon's x. The
     comfortable hit area comes from the inset pseudo-element below, never from padding. */
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: none;
  color: ${COLORS.textMuted};

  &::after {
    content: '';
    position: absolute;
    inset: -6px;
  }

  &:hover {
    color: ${COLORS.textStrong};
    background: ${COLORS.hoverWash};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: default;
    color: ${COLORS.textFaint};
  }
`;

/** "Regenerate", alone in the trailing slot: a bare text button in the CTA colour. */
export const RegenerateLink = styled.button`
  appearance: none;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 16px;
  font-family: inherit;
  color: ${COLORS.ctaSolid};
  white-space: nowrap;

  &:focus-visible {
    outline: 2px solid ${COLORS.ctaSolid};
    outline-offset: 2px;
    border-radius: 4px;
  }

  &:disabled {
    cursor: default;
    color: ${COLORS.textFaint};
  }
`;

/** The copy button's stand-in while there is nothing to copy (no plaintext held on the mailbox page):
 *  the shared CopyButton's exact box (14px glyph in 6px padding, radius 7) so the row does not move,
 *  disabled, titled. No create-form row uses it now: the mailbox page's CredentialBlock imports it from
 *  here, so every credential row draws one copy box. Do not remove it with a create-form change. */
export const CopyDisabled = styled.button`
  appearance: none;
  display: inline-grid;
  place-items: center;
  /* The shared CopyButton's geometry, so the two copy controls are the same box. */
  padding: 6px;
  border: 0;
  border-radius: 7px;
  background: none;
  line-height: 0;
  color: ${COLORS.textFaint};
  cursor: not-allowed;
`;

/** A field with what sits under it (its message line, a meter), on the form's field gap. */
export const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/** Inline validation message under a field. */
export const FieldError = styled(Text)`
  font-size: 12px;
  color: ${DELTA_PILL.negative.fg};
`;
